import { CheckMarkIcon } from '@/common/icons/arrows.tsx'
import { cn } from '@/lib/utils.ts'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { ReactNode } from 'react'

type BaseListItem = { id: number; name: string }

type SimpleSelectorProps<ListItem extends BaseListItem> = {
  list: ListItem[]
  selected: ListItem | null
  setSelected: (listItem: ListItem) => void
  className?: string
  icon: ReactNode
  isDisabled?: boolean
  placeholder?: string
}

export default function SimpleSelector<ListItem extends BaseListItem>({
  list,
  selected,
  setSelected,
  className,
  icon,
  isDisabled = false,
  placeholder = '',
}: SimpleSelectorProps<ListItem>) {
  return (
    <Listbox value={selected} onChange={setSelected}>
      <div className={cn('relative', className)}>
        <ListboxButton
          disabled={isDisabled}
          className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:opacity-50"
        >
          <span
            className={cn(
              'block truncate',
              selected?.name == null && 'min-h-6',
              selected == null && placeholder && 'text-gray-400'
            )}
          >
            {selected?.name ?? placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            {icon}
          </span>
        </ListboxButton>

        <ListboxOptions
          transition
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
        >
          {list.map((listItem) => (
            <ListboxOption
              key={listItem.id}
              value={listItem}
              className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary-600 data-[focus]:text-white"
            >
              <span className="block font-normal group-data-[selected]:font-semibold">
                {listItem.name}
              </span>

              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                <CheckMarkIcon />
              </span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  )
}
